import { useState } from "react";
import { Plus, Trash2, Save, Pencil, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  usePartTypes,
  usePartTypeMutations,
  useLinkedInstanceCount,
  type SparePartType,
} from "@/hooks/useSparePartTypes";
import AliasChipEditor from "./AliasChipEditor";
import TagMultiSelect from "./TagMultiSelect";

interface Props {
  partsCategoryId: string;
  partsCategoryName: string;
}

interface Draft {
  name: string;
  description: string;
  default_weight_kg: string;
  default_value: string;
  default_recovery_pct: string;
  is_hazardous: boolean;
}

const empty: Draft = {
  name: "", description: "", default_weight_kg: "", default_value: "", default_recovery_pct: "", is_hazardous: false,
};

function toDraft(t: SparePartType): Draft {
  return {
    name: t.name,
    description: t.description ?? "",
    default_weight_kg: t.default_weight_kg != null ? String(t.default_weight_kg) : "",
    default_value: t.default_value != null ? String(t.default_value) : "",
    default_recovery_pct: t.default_recovery_pct != null ? String(t.default_recovery_pct) : "",
    is_hazardous: !!t.is_hazardous,
  };
}

function toPayload(d: Draft) {
  const num = (v: string) => (v === "" ? null : Number(v));
  return {
    name: d.name.trim(),
    description: d.description.trim() || null,
    default_weight_kg: num(d.default_weight_kg),
    default_value: num(d.default_value),
    default_recovery_pct: num(d.default_recovery_pct),
    is_hazardous: d.is_hazardous,
  };
}

function LinkedCount({ partTypeId }: { partTypeId: string }) {
  const { data: count = 0 } = useLinkedInstanceCount(partTypeId);
  return <span className="text-xs text-muted-foreground">{count} actual part{count === 1 ? "" : "s"} linked</span>;
}

export default function PartTypesEditor({ partsCategoryId, partsCategoryName }: Props) {
  const { data: types = [], isLoading } = usePartTypes(partsCategoryId);
  const { create, update, remove } = usePartTypeMutations(partsCategoryId);
  const [showNew, setShowNew] = useState(false);
  const [newDraft, setNewDraft] = useState<Draft>(empty);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(empty);

  const formGrid = (d: Draft, set: (x: Draft) => void) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      <Input placeholder="Part type name *" value={d.name} onChange={(e) => set({ ...d, name: e.target.value })} className="h-8 text-sm" />
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={d.is_hazardous} onCheckedChange={(c) => set({ ...d, is_hazardous: !!c })} />
        Hazardous
      </label>
      <Input placeholder="Weight kg (override)" type="number" step="0.1" value={d.default_weight_kg} onChange={(e) => set({ ...d, default_weight_kg: e.target.value })} className="h-8 text-sm" />
      <Input placeholder="Value € (override)" type="number" step="0.01" value={d.default_value} onChange={(e) => set({ ...d, default_value: e.target.value })} className="h-8 text-sm" />
      <Input placeholder="Recovery % (override)" type="number" min="0" max="100" value={d.default_recovery_pct} onChange={(e) => set({ ...d, default_recovery_pct: e.target.value })} className="h-8 text-sm" />
      <Textarea placeholder="Description" value={d.description} onChange={(e) => set({ ...d, description: e.target.value })} rows={1} className="md:col-span-2 text-sm" />
    </div>
  );

  return (
    <div className="bg-muted/20 border-t border-border px-4 py-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Part Types under {partsCategoryName}
        </div>
        {!showNew && (
          <Button size="sm" variant="outline" onClick={() => setShowNew(true)} className="h-7 gap-1 text-xs">
            <Plus className="w-3.5 h-3.5" /> Add Part Type
          </Button>
        )}
      </div>

      {showNew && (
        <div className="bg-card border border-border rounded-lg p-3 space-y-2">
          {formGrid(newDraft, setNewDraft)}
          <div className="flex gap-2">
            <Button size="sm" disabled={!newDraft.name.trim() || create.isPending}
              onClick={async () => {
                await create.mutateAsync(toPayload(newDraft) as any);
                setNewDraft(empty); setShowNew(false);
              }}>
              <Save className="w-3.5 h-3.5 mr-1" />Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowNew(false); setNewDraft(empty); }}>
              <X className="w-3.5 h-3.5 mr-1" />Cancel
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-xs text-muted-foreground py-2">Loading part types…</div>
      ) : types.length === 0 ? (
        <div className="text-xs text-muted-foreground py-2">
          No part types defined yet. Add e.g. "PCB / Main Control Board", "Drain Pump"…
        </div>
      ) : (
        <div className="space-y-2">
          {types.map((t) => (
            <div key={t.id} className="bg-card border border-border rounded-lg p-3 space-y-2">
              {editingId === t.id ? (
                <>
                  {formGrid(editDraft, setEditDraft)}
                  <div className="flex gap-2">
                    <Button size="sm" disabled={!editDraft.name.trim() || update.isPending}
                      onClick={async () => {
                        await update.mutateAsync({ id: t.id, patch: toPayload(editDraft) as any });
                        setEditingId(null);
                      }}>
                      <Save className="w-3.5 h-3.5 mr-1" />Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                      <X className="w-3.5 h-3.5 mr-1" />Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{t.name}</span>
                        {t.is_hazardous && <Badge variant="destructive" className="text-[10px]">Hazardous</Badge>}
                        <LinkedCount partTypeId={t.id} />
                      </div>
                      {t.description && <div className="text-xs text-muted-foreground mt-0.5">{t.description}</div>}
                      <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-x-3">
                        {t.default_weight_kg != null && <span>{t.default_weight_kg} kg</span>}
                        {t.default_value != null && <span>€ {t.default_value}</span>}
                        {t.default_recovery_pct != null && <span>{t.default_recovery_pct}% recovery</span>}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                        onClick={() => { setEditingId(t.id); setEditDraft(toDraft(t)); }}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        onClick={() => { if (confirm(`Remove "${t.name}"? This unlinks all spare parts from this type.`)) remove.mutate(t.id); }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-1.5 pt-2 border-t border-dashed">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Aliases</div>
                    <AliasChipEditor partTypeId={t.id} />
                  </div>
                  <div className="space-y-1.5">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Tags</div>
                    <TagMultiSelect partTypeId={t.id} />
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
